import { CouponDealTypeEnum } from "@saleor/types/globalTypes";
import moment from "moment-timezone";
const isEmpty = field =>
  JSON.stringify(field) === "" || field === null || field === "";

const defaultErrorMessage = "This field is required.";
const alphanumericRegex = /^[a-zA-Z0-9]+$/;
const couponValueRegex = /^\d+(\.\d{1,2})?$/;
const timerIsEmpty = timer => timer === "";
const zeroTimerValue = timer =>
  timer.toString() === "0" || timer.toString() === "00";

const validateRequiredField = data => {
  if (isEmpty(data)) {
    return "This field is required.";
  }

  return null;
};

const validateDraftRequireField = data => {
  if (!data) {
    return "This field is required.";
  }

  return null;
};

export const createCouponValidation = (
  initialForm,
  options?: { canManageCouponUsageLimit?: boolean }
) => {
  const {
    assignee,
    couponCode,
    mainPromoDescription,
    mainPromoTitle,
    mechanics,
    subtitle,
    tagline,
    category,
    eventStartDate,
    eventLocation,
    couponClassification,
    accessPageDescription,
    couponStartDate,
    couponStartTime,
    couponEndDate,
    couponEndTime,
    couponValue,
    isUsageCustomTimer,
    occurrence,
    redeemType,
    redeemingStartDate,
    redeemingStartTime,
    redeemingEndDate,
    redeemingEndTime,
    excludedDays,
    totalCopies,
    usageLimit,
    usageLimitToggle,
    usageTimerHour,
    usageTimerMinute,
    usageTimerSecond,
    accessImage,
    promoImage
  } = initialForm;

  // Default true so callers that manage the field still validate.
  // When false (no MANAGE_COUPON_USAGE_LIMIT), skip — field is hidden and must not block publish.
  const canManageCouponUsageLimit =
    options?.canManageCouponUsageLimit !== false;

  const imageValidations = () => {
    if (!accessImage && !promoImage) {
      return "Promo Image and Access Image must be provided";
    }
    if (!promoImage) {
      return "Promo Image must be provided";
    }
    if (!accessImage) {
      return "Access Image must be provided";
    }
    return null;
  };

  const couponCodeValidation = () => {
    if (isEmpty(couponCode)) {
      return "This field is required.";
    }
    return null;
  };

  const couponValueValidation = () => {
    if (isEmpty(couponValue)) {
      return "This field is required.";
    }
    return null;
  };

  const couponStartDateMessage = () => {
    if (isEmpty(couponStartDate)) {
      return defaultErrorMessage;
    }

    return null;
  };

  const couponStartTimeMessage = () => {
    if (isEmpty(couponStartTime)) {
      return defaultErrorMessage;
    }

    return null;
  };

  const couponEndDateMessage = () => {
    if (isEmpty(couponEndDate)) {
      return defaultErrorMessage;
    }

    if (
      isEmpty(!couponStartDate) &&
      moment(couponEndDate).isBefore(moment(couponStartDate))
    ) {
      return "Coupon end date should be greater than or equal to start date.";
    }

    return null;
  };

  const couponEndTimeMessage = () => {
    if (isEmpty(couponEndTime)) {
      return defaultErrorMessage;
    }

    const hasCouponStart = !!couponStartDate && !!couponStartTime;

    if (hasCouponStart && !!couponEndDate) {
      const couponStartMoment = moment(
        couponStartDate + " " + couponStartTime,
        "YYYY-MM-DD HH:mm"
      );
      const couponEndMoment = moment(
        couponEndDate + " " + couponEndTime,
        "YYYY-MM-DD HH:mm"
      );
      const isPast = couponEndMoment.isBefore(
        couponStartMoment.add(1, "minutes")
      );

      if (isPast) {
        return "Coupon end date and time should be after start date and time.";
      }
    }

    return null;
  };

  const redeemingStartDateMessage = () => {
    if (isEmpty(redeemingStartDate)) {
      return defaultErrorMessage;
    }

    const isPast = moment(redeemingStartDate).isBefore(moment(couponStartDate));
    if (isEmpty(!couponStartDate) && isPast) {
      return "Redeeming start date should not be less than claiming schedule date.";
    }

    return null;
  };

  const redeemingStartTimeMessage = () => {
    if (isEmpty(redeemingStartTime)) {
      return defaultErrorMessage;
    }

    const hasCouponSchedule =
      !!couponStartDate &&
      !!couponStartTime &&
      !!couponEndDate &&
      !!couponEndTime &&
      !!redeemingStartDate;

    if (hasCouponSchedule) {
      const couponStartMoment = moment(
        couponStartDate + " " + couponStartTime,
        "YYYY-MM-DD HH:mm"
      );
      const isPast = moment(
        redeemingStartDate + " " + redeemingStartTime,
        "YYYY-MM-DD HH:mm"
      ).isBefore(couponStartMoment);

      if (isPast) {
        return "Redeeming start date and time should not be less than claiming schedule start date and time.";
      }
    }

    return null;
  };

  const redeemingEndDateMessage = () => {
    if (isEmpty(redeemingEndDate)) {
      return defaultErrorMessage;
    }

    const hasCouponSchedule =
      !!couponStartDate &&
      !!couponStartTime &&
      !!couponEndDate &&
      !!couponEndTime;
    const hasRedeemingStart = !!redeemingStartDate && !!redeemingStartTime;

    if (hasCouponSchedule && hasRedeemingStart) {
      const redeemingStartMoment = moment(redeemingStartDate);
      const isPast = moment(redeemingEndDate).isBefore(redeemingStartMoment);

      if (isPast) {
        return "Redeeming end date should be greater than or equal to start date.";
      }
    }

    return null;
  };

  const redeemingEndTimeMessage = () => {
    if (isEmpty(redeemingEndTime)) {
      return defaultErrorMessage;
    }

    const hasCouponSchedule =
      !!couponStartDate &&
      !!couponStartTime &&
      !!couponEndDate &&
      !!couponEndTime;
    const hasRedeemingStart = !!redeemingStartDate && !!redeemingStartTime;

    if (hasCouponSchedule && hasRedeemingStart) {
      const redeemingStartMoment = moment(
        redeemingStartDate + " " + redeemingStartTime,
        "YYYY-MM-DD HH:mm"
      ).add(1, "minutes");
      const isPast = moment(
        redeemingEndDate + " " + redeemingEndTime,
        "YYYY-MM-DD HH:mm"
      ).isBefore(redeemingStartMoment);

      if (isPast) {
        return "Redeeming end date and time should be after start date and time.";
      }
    }

    return null;
  };

  const excludedDaysMessage = () => {
    if (!isEmpty(excludedDays) && excludedDays.length === 7) {
      return "You should allow at least one day.";
    }

    return null;
  };

  const usageTimerLimitMessage = () => {
    if (isUsageCustomTimer) {
      if (
        timerIsEmpty(usageTimerHour) ||
        timerIsEmpty(usageTimerMinute) ||
        timerIsEmpty(usageTimerSecond)
      ) {
        return "Set Custom Timer must not be empty.";
      } else if (
        zeroTimerValue(usageTimerHour) &&
        zeroTimerValue(usageTimerMinute) &&
        zeroTimerValue(usageTimerSecond)
      ) {
        return "All 00 values are unacceptable for Set Custom Timer.";
      } else if (
        usageTimerHour > 23 ||
        usageTimerMinute > 59 ||
        usageTimerSecond > 59
      ) {
        return "Timer limit should not exceed 23 hours 59 minutes and 59 seconds";
      }
    }

    return null;
  };

  const usageLimitMessage = () => {
    // Usage Limit UI is hidden without permission — do not block save/publish.
    // Existing coupon values (e.g. 10) are retained by submit payload logic.
    if (!canManageCouponUsageLimit) {
      return null;
    }

    if (
      (usageLimitToggle === "multiple" && usageLimit <= 0) ||
      isEmpty(usageLimit) ||
      usageLimit === undefined
    ) {
      return "Coupon usage limit must be greater than 0";
    }

    return null;
  };

  const eventLocationMessage = () => {
    if (
      isEmpty(eventLocation) &&
      couponClassification === CouponDealTypeEnum.EVENTS
    ) {
      return "This field is required";
    }
    return null;
  };

  const categoryMessage = () => {
    if (
      isEmpty(category) &&
      couponClassification !== CouponDealTypeEnum.EVENTS
    ) {
      return "This field is required";
    }
    return null;
  };

  const eventStartDateMessage = () => {
    if (
      isEmpty(eventStartDate) &&
      couponClassification === CouponDealTypeEnum.EVENTS
    ) {
      return defaultErrorMessage;
    }

    const redeemingStart = moment(redeemingStartDate);
    if (redeemingStart.isAfter(moment(eventStartDate), "day")) {
      return "Event start date should be on or after the Redeeming Start Date.";
    }
    return null;
  };

  const couponAssigneeMessage = () => {
    if (isEmpty(assignee)) {
      return defaultErrorMessage;
    }
    return null;
  };

  return {
    accessPageDescription: validateRequiredField(accessPageDescription),
    assignee: couponAssigneeMessage(),
    category: categoryMessage(),
    couponCode: couponCodeValidation(),
    couponEndDate: couponEndDateMessage(),
    couponEndTime: couponEndTimeMessage(),
    couponStartDate: couponStartDateMessage(),
    couponStartTime: couponStartTimeMessage(),
    couponValue: couponValueValidation(),
    eventLocation: eventLocationMessage(),
    eventStartDate: eventStartDateMessage(),
    excludedDays: excludedDaysMessage(),
    formDataValidations: formDataValidations(initialForm),
    imageUpload: imageValidations(),
    mainPromoDescription: validateRequiredField(mainPromoDescription),
    mainPromoTitle: validateRequiredField(mainPromoTitle),
    mechanics: validateRequiredField(mechanics),
    occurrence: validateRequiredField(occurrence),
    redeemType: validateRequiredField(redeemType),
    redeemingEndDate: redeemingEndDateMessage(),
    redeemingEndTime: redeemingEndTimeMessage(),
    redeemingStartDate: redeemingStartDateMessage(),
    redeemingStartTime: redeemingStartTimeMessage(),
    subtitle: validateRequiredField(subtitle),
    tagline: validateRequiredField(tagline),
    totalCopies: validateRequiredField(totalCopies),
    usageLimit: usageLimitMessage(),
    usageTimerHour: usageTimerLimitMessage(),
    usageTimerMinute: usageTimerLimitMessage(),
    usageTimerSecond: usageTimerLimitMessage()
  };
};

export const draftCouponCreateValidation = initialForm => {
  const {
    couponStartDate,
    couponStartTime,
    couponEndDate,
    couponEndTime,
    eventStartDate,
    isUsageCustomTimer,
    mainPromoTitle,
    redeemingStartDate,
    redeemingStartTime,
    redeemingEndDate,
    redeemingEndTime,
    usageTimerHour,
    usageTimerMinute,
    usageTimerSecond
  } = initialForm;

  const couponEndDateMessage = () => {
    if (couponStartDate && couponEndDate < couponStartDate) {
      return "Coupon end date should be greater than or equal to start date.";
    }

    return null;
  };

  const couponEndTimeMessage = () => {
    const hasCouponStart = !!couponStartDate && !!couponStartTime;

    if (hasCouponStart && !!couponEndDate) {
      const couponStartMoment = moment(
        couponStartDate + " " + couponStartTime,
        "YYYY-MM-DD HH:mm"
      );
      const couponEndMoment = moment(
        couponEndDate + " " + couponEndTime,
        "YYYY-MM-DD HH:mm"
      );
      const isPast = couponEndMoment.isBefore(
        couponStartMoment.add(1, "minutes")
      );

      if (isPast) {
        return "Coupon end date and time should be after start date and time.";
      }
    }

    return null;
  };

  const redeemingStartDateMessage = () => {
    const isPast = moment(redeemingStartDate).isBefore(moment(couponStartDate));
    if (!couponStartDate && isPast) {
      return "Redeeming start date should not be less than claiming schedule date.";
    }

    return null;
  };

  const redeemingStartTimeMessage = () => {
    const hasCouponSchedule =
      !!couponStartDate &&
      !!couponStartTime &&
      !!couponEndDate &&
      !!couponEndTime &&
      !!redeemingStartDate;

    if (hasCouponSchedule) {
      const couponStartMoment = moment(
        couponStartDate + " " + couponStartTime,
        "YYYY-MM-DD HH:mm"
      );
      const isPast = moment(
        redeemingStartDate + " " + redeemingStartTime,
        "YYYY-MM-DD HH:mm"
      ).isBefore(couponStartMoment);

      if (isPast) {
        return "Redeeming start date and time should not be less than claiming schedule start date and time.";
      }
    }

    return null;
  };

  const redeemingEndDateMessage = () => {
    const hasCouponSchedule =
      !!couponStartDate &&
      !!couponStartTime &&
      !!couponEndDate &&
      !!couponEndTime;
    const hasRedeemingStart = !!redeemingStartDate && !!redeemingStartTime;

    if (hasCouponSchedule && hasRedeemingStart) {
      const redeemingStartMoment = moment(redeemingStartDate);
      const isPast = moment(redeemingEndDate).isBefore(redeemingStartMoment);

      if (isPast) {
        return "Redeeming end date should be greater than or equal to start date.";
      }
    }

    return null;
  };

  const redeemingEndTimeMessage = () => {
    const hasCouponSchedule =
      !!couponStartDate &&
      !!couponStartTime &&
      !!couponEndDate &&
      !!couponEndTime;
    const hasRedeemingStart = !!redeemingStartDate && !!redeemingStartTime;

    if (hasCouponSchedule && hasRedeemingStart) {
      const redeemingStartMoment = moment(
        redeemingStartDate + " " + redeemingStartTime,
        "YYYY-MM-DD HH:mm"
      ).add(1, "minutes");
      const isPast = moment(
        redeemingEndDate + " " + redeemingEndTime,
        "YYYY-MM-DD HH:mm"
      ).isBefore(redeemingStartMoment);

      if (isPast) {
        return "Redeeming end date and time should be after start date and time.";
      }
    }

    return null;
  };

  const usageTimerLimitMessage = () => {
    if (isUsageCustomTimer) {
      if (
        timerIsEmpty(usageTimerHour) ||
        timerIsEmpty(usageTimerMinute) ||
        timerIsEmpty(usageTimerSecond)
      ) {
        return "Set Custom Timer must not be empty.";
      } else if (
        zeroTimerValue(usageTimerHour) &&
        zeroTimerValue(usageTimerMinute) &&
        zeroTimerValue(usageTimerSecond)
      ) {
        return "All 00 values are unacceptable for Set Custom Timer.";
      }
    }

    return null;
  };

  const eventStartDateMessage = () => {
    if (isEmpty(eventStartDate)) {
      return null;
    }

    const redeemingStart = moment(redeemingStartDate);
    if (redeemingStart.isAfter(moment(eventStartDate), "day")) {
      return "Event start date should be on or after the Redeeming Start Date.";
    }
    return null;
  };

  return {
    couponEndDate: couponEndDateMessage(),
    couponEndTime: couponEndTimeMessage(),
    eventStartDate: eventStartDateMessage(),
    mainPromoTitle: validateDraftRequireField(mainPromoTitle),
    redeemingEndDate: redeemingEndDateMessage(),
    redeemingEndTime: redeemingEndTimeMessage(),
    redeemingStartDate: redeemingStartDateMessage(),
    redeemingStartTime: redeemingStartTimeMessage(),
    totalCopies: null,
    usageTimerHour: usageTimerLimitMessage(),
    usageTimerMinute: usageTimerLimitMessage(),
    usageTimerSecond: usageTimerLimitMessage()
  };
};

export const formDataValidations = initialForm => {
  const { couponCode, couponValue, totalCopies } = initialForm;

  if (couponCode && !alphanumericRegex.test(couponCode)) {
    return {
      status: "error",
      text: "Coupon Code must contain only alphanumeric characters",
      title: "Coupon Form Error"
    };
  }
  if (couponValue) {
    if (parseFloat(couponValue) <= 0) {
      return {
        status: "error",
        text: "Discounted Price must be greater than 0",
        title: "Can't Submit"
      };
    }
    if (!couponValueRegex.test(couponValue)) {
      return {
        status: "error",
        text: "Discounted Price can only accept two decimal places.",
        title: "Can't Submit"
      };
    }
    if (totalCopies) {
      if (parseFloat(totalCopies) <= 0) {
        return {
          status: "error",
          text: "Coupon Copies must be greater than 0",
          title: "Can't Submit"
        };
      }
    }
  }

  return null;
};

export const hasNoError = validationError => {
  let thisReturn = true;
  Object.keys(validationError).forEach(key => {
    if (thisReturn) {
      thisReturn = validationError[key] === null;
    }
  });
  return thisReturn;
};
